import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "design-system/molecules/Card"
import { motion } from "framer-motion"
import { RadarIcon } from "lucide-react"
import { SentimentChart } from "modules/customers/components/Detail/Profile/SentimentChart"
import { Loading } from "modules/shared/components/Loading"
import { useTranslation } from "react-i18next"
import { GetSentimentResponseDataSchema } from "schema"
import { z } from "zod"

type Props = {
  customerSentiment: z.infer<typeof GetSentimentResponseDataSchema>
  isPending?: boolean
}

export const CustomerSentimentChart = ({ customerSentiment, isPending }: Props) => {
  const { t } = useTranslation()

  if (isPending) {
    return (
      <Card className="flex flex-col h-full w-full border-b-0 rounded-b-none sm:border-b-1 sm:rounded-bl-xl sm:rounded-r-none sm:border-r-0">
        <CardHeader className="flex-row items-center w-full px-4 pt-3 pb-2">
          <div className="flex flex-col gap-2">
            <CardTitle className="text-sm">{t("customers.sentiment.chart.title")}</CardTitle>
            <CardDescription>{t("customers.sentiment.chart.description")}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-row flex-grow items-center justify-center px-4 pb-3">
          <Loading />
        </CardContent>
      </Card>
    )
  }

  if (!customerSentiment?.sentiment.length) {
    return (
      <Card className="flex flex-col h-full w-full border-b-0 rounded-b-none sm:border-b-1 sm:rounded-bl-xl sm:rounded-r-none sm:border-r-0">
        <CardHeader className="flex-row items-center w-full px-4 pt-3 pb-2">
          <div className="flex flex-col gap-2">
            <CardTitle className="text-sm">{t("customers.sentiment.chart.title")}</CardTitle>
            <CardDescription>{t("customers.sentiment.chart.description")}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center flex-grow px-4 pb-3">
          <div className="flex flex-col gap-2 py-8 items-center justify-center text-center m-auto max-w-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <RadarIcon className="size-8 text-muted-foreground mb-2" strokeWidth={1.5} />
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-sm font-medium text-foreground">
                {t("customers.sentiment.chart.noData.title")}
              </h2>
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <p className="text-sm text-muted-foreground">
                {t("customers.sentiment.chart.noData.description")}
              </p>
            </motion.div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex flex-col w-full h-full border-b-0 rounded-b-none sm:border-b-1 sm:rounded-bl-xl sm:rounded-r-none sm:border-r-0">
      <CardHeader className="flex-row items-start justify-between w-full px-4 pt-3 pb-2 gap-4">
        <div className="flex flex-col gap-2">
          <CardTitle className="text-sm">{t("customers.sentiment.chart.title")}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col px-4 pt-2 pb-3 overflow-x-auto flex-grow">
        <SentimentChart sentiment={customerSentiment.sentiment} />
      </CardContent>
    </Card>
  )
}
